import { Field, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Stack, TextInput } from '@carbon/react';
import { suggestSlug, useCreateOrganization, useSlugAvailability } from 'src/hooks/useOrganizations';
import StepLayout from './StepLayout';
import { organizationSchema } from './schemas';

const OrganizationStep = ({ onDone }: { onDone: () => void }) => {
  const { t } = useTranslation('authentication');
  const [slugTouched, setSlugTouched] = useState(false);
  const [slug, setSlug] = useState('');
  const { data: slugFree } = useSlugAvailability(slug);
  const createOrganization = useCreateOrganization();

  async function onSubmit(values: { name: string; slug: string }) {
    await createOrganization.mutateAsync(values);
    onDone();
  }

  return (
    <Formik
      initialValues={{ name: '', slug: '' }}
      validationSchema={toFormikValidationSchema(organizationSchema(t))}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, isSubmitting, isValid, dirty, values, setFieldValue }) => (
        <StepLayout
          title={t('onboarding.organization.title')}
          subtitle={t('onboarding.organization.subtitle')}
          help={t('onboarding.organization.help')}
          error={(createOrganization.error as Error) ?? null}
          onSubmit={handleSubmit}
          actions={
            <Button type="submit" disabled={isSubmitting || !isValid || !dirty || slugFree === false}>
              {t('onboarding.continue')}
            </Button>
          }
        >
          <SlugSuggestion
            name={values.name}
            touched={slugTouched}
            onSuggest={(next) => {
              // Both sinks: Formik owns the field, `slug` drives the availability query.
              setFieldValue('slug', next);
              setSlug(next);
            }}
          />
          <Stack gap={5}>
            <Field name="name">
              {({ field, meta }) => (
                <TextInput
                  id="org-name"
                  labelText={t('onboarding.organization.name')}
                  invalid={!!(meta.touched && meta.error)}
                  invalidText={meta.error}
                  {...field}
                />
              )}
            </Field>
            <Field name="slug">
              {({ field, meta }) => (
                <TextInput
                  id="org-slug"
                  labelText={t('onboarding.organization.slug')}
                  helperText={t('onboarding.organization.slugHint', { slug: values.slug })}
                  invalid={!!(meta.touched && meta.error) || (values.slug.length > 2 && slugFree === false)}
                  invalidText={meta.error || t('onboarding.organization.slugTaken')}
                  {...field}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setSlug(e.target.value);
                    field.onChange(e);
                  }}
                />
              )}
            </Field>
          </Stack>
        </StepLayout>
      )}
    </Formik>
  );
};

/**
 * Until the user edits the slug themselves it tracks the name and auto-suffixes past any
 * collision — the field is a suggestion, never a blocker. Kept as a child component so
 * the effect can call Formik's `setFieldValue` without the parent re-running on it.
 */
const SlugSuggestion = ({
  name,
  touched,
  onSuggest,
}: {
  name: string;
  touched: boolean;
  onSuggest: (slug: string) => void;
}) => {
  useEffect(() => {
    if (touched || name.length < 2) return;
    let cancelled = false;
    suggestSlug(name).then((next) => {
      if (!cancelled) onSuggest(next);
    });
    return () => {
      cancelled = true;
    };
    // `onSuggest` is a fresh closure each render; depending on it would loop.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [name, touched]);

  return null;
};

export default OrganizationStep;
